import * as React from 'react';
import {
    Box, Card, CardContent, Typography, Grid, Avatar, Chip,
    CircularProgress, Alert, Accordion, AccordionSummary,
    AccordionDetails, Divider, IconButton, Tooltip, Badge,
    Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Paper, Button, Stack
} from '@mui/material';
import { useNotify } from 'react-admin';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ComputerIcon from '@mui/icons-material/Computer';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { motion } from 'framer-motion';
import axios from 'axios';
import config from '../../../config/config.json';

const host = config.production.host;

// Funzione per recuperare il token JWT
const getJwt = () => {
    return localStorage.getItem("feathers-jwt");
};

// Formatta la data italiana
const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    });
};

// Componente per mostrare i cambiamenti
const ChangeComparison = ({ previousData, updatedData, patchData }) => {
    const changedFields = Object.keys(patchData || {});

    if (changedFields.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Nessun campo modificato rilevato
            </Typography>
        );
    }

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'primary.main' }}>
                Campi modificati:
            </Typography>

            {changedFields.map((field) => (
                <Box key={field} sx={{ mb: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 1, border: '1px solid #e9ecef' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        {field}
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">
                                Valore precedente:
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    bgcolor: '#ffebee',
                                    p: 1,
                                    borderRadius: 0.5,
                                    border: '1px solid #ffcdd2',
                                    fontFamily: 'monospace',
                                    fontSize: '0.8rem'
                                }}
                            >
                                {JSON.stringify(previousData?.[field] ?? 'N/A')}
                            </Typography>
                        </Grid>

                        <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">
                                Nuovo valore:
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    bgcolor: '#e8f5e8',
                                    p: 1,
                                    borderRadius: 0.5,
                                    border: '1px solid #c8e6c9',
                                    fontFamily: 'monospace',
                                    fontSize: '0.8rem'
                                }}
                            >
                                {JSON.stringify(updatedData?.[field] ?? patchData[field] ?? 'N/A')}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>
            ))}
        </Box>
    );
};

export const TeamActivityLog = ({ teamId, teamName }) => {
    const notify = useNotify();

    // Stati
    const [activities, setActivities] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [totalRecords, setTotalRecords] = React.useState(0);

    // Carica il log delle attività
    const loadActivityLog = async () => {
        try {
            setLoading(true);
            setError(null);
            const jwt = getJwt();

            const response = await axios.get(
                `${host}/proc-get-log-activities`,
                {
                    headers: { 'Authorization': `Bearer ${jwt}` },
                    params: {
                        recordId_dal: parseInt(teamId),
                        recordId_al: parseInt(teamId),
                        method: 'PATCH',
                        service: 'tbl-team'
                    }
                }
            );

            const data = response.data;
            if (data.esito === 0) {
                setActivities(data.data || []);
                setTotalRecords(data.numero_record_trovati || 0);
            } else {
                setError(`Errore dal server: ${data.error_message || 'Errore sconosciuto'}`);
            }

        } catch (error) {
            console.error("Errore nel recupero log attività:", error);
            setError("Errore nel caricamento del log delle attività");
            notify("Errore nel caricamento del log", { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // Carica i dati al mount e quando cambia teamId
    React.useEffect(() => {
        if (teamId) {
            loadActivityLog();
        }
    }, [teamId]);

    // Raggruppa le attività per data
    const groupActivitiesByDate = (activities) => {
        return activities.reduce((groups, activity) => {
            const date = new Date(activity.createdAt).toLocaleDateString('it-IT');
            if (!groups[date]) {
                groups[date] = [];
            }
            groups[date].push(activity);
            return groups;
        }, {});
    };

    const groupedActivities = groupActivitiesByDate(activities);

    // Ottieni lo status dell'attività
    const getActivityStatus = (status) => {
        switch (status) {
            case 'SUCCESS':
                return { label: 'Successo', color: 'success', icon: <CheckCircleIcon /> };
            case 'ERROR':
                return { label: 'Errore', color: 'error', icon: <ErrorIcon /> };
            default:
                return { label: status, color: 'default', icon: <HistoryIcon /> };
        }
    };

    // Loading state
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2 }}>
                    Caricamento log attività...
                </Typography>
            </Box>
        );
    }

    // Error state
    if (error) {
        return (
            <Alert
                severity="error"
                sx={{ m: 2 }}
                action={
                    <IconButton color="inherit" size="small" onClick={loadActivityLog}>
                        <RefreshIcon />
                    </IconButton>
                }
            >
                {error}
            </Alert>
        );
    }

    // No activities found
    if (activities.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 8 }}>
                <HistoryIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                    Nessuna attività trovata
                </Typography>
                <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
                    Non ci sono modifiche registrate per questa squadra
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            {/* Header con statistiche */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                        Log Attività - {teamName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Totale modifiche: {totalRecords} operazioni PATCH
                    </Typography>
                </Box>

                <Tooltip title="Aggiorna log">
                    <IconButton onClick={loadActivityLog} color="primary">
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Statistiche rapide */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} md={3}>
                    <Card sx={{ textAlign: 'center', bgcolor: '#e3f2fd', border: '1px solid #2196f320' }}>
                        <CardContent sx={{ py: 2 }}>
                            <HistoryIcon sx={{ color: '#1976d2', mb: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                                {totalRecords}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Modifiche totali
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={6} md={3}>
                    <Card sx={{ textAlign: 'center', bgcolor: '#e8f5e8', border: '1px solid #4caf5020' }}>
                        <CardContent sx={{ py: 2 }}>
                            <CheckCircleIcon sx={{ color: '#388e3c', mb: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#388e3c' }}>
                                {activities.filter(a => a.status === 'SUCCESS').length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Successi
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={6} md={3}>
                    <Card sx={{ textAlign: 'center', bgcolor: '#fff3e0', border: '1px solid #ff980020' }}>
                        <CardContent sx={{ py: 2 }}>
                            <PersonIcon sx={{ color: '#f57c00', mb: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#f57c00' }}>
                                {new Set(activities.map(a => a.user_email).filter(Boolean)).size}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Utenti diversi
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={6} md={3}>
                    <Card sx={{ textAlign: 'center', bgcolor: '#f3e5f5', border: '1px solid #9c27b020' }}>
                        <CardContent sx={{ py: 2 }}>
                            <AccessTimeIcon sx={{ color: '#7b1fa2', mb: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#7b1fa2' }}>
                                {Object.keys(groupedActivities).length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Giorni attivi
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Timeline delle attività raggruppate per data */}
            <Box sx={{ space: 2 }}>
                {Object.entries(groupedActivities).map(([date, dateActivities], dateIndex) => (
                    <motion.div
                        key={date}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: dateIndex * 0.1 }}
                    >
                        <Accordion defaultExpanded={dateIndex === 0} sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                    bgcolor: '#f8f9fa',
                                    borderLeft: '4px solid #1976d2',
                                    '&:hover': { bgcolor: '#e9ecef' }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                    <AccessTimeIcon sx={{ color: '#1976d2' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        {date}
                                    </Typography>
                                    <Badge
                                        badgeContent={dateActivities.length}
                                        color="primary"
                                        sx={{ ml: 'auto' }}
                                    />
                                </Box>
                            </AccordionSummary>

                            <AccordionDetails sx={{ p: 0 }}>
                                {dateActivities.map((activity, activityIndex) => {
                                    const status = getActivityStatus(activity.status);
                                    const details = activity.details || {};

                                    return (
                                        <motion.div
                                            key={activity.id}
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: activityIndex * 0.05 }}
                                        >
                                            <Card
                                                sx={{
                                                    m: 2,
                                                    borderRadius: 2,
                                                    border: '1px solid #e0e0e0',
                                                    transition: 'all 0.2s',
                                                    '&:hover': {
                                                        boxShadow: 2,
                                                        borderColor: '#1976d2'
                                                    }
                                                }}
                                            >
                                                <CardContent sx={{ p: 3 }}>
                                                    {/* Header dell'attività */}
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                                        <Avatar
                                                            sx={{
                                                                width: 40,
                                                                height: 40,
                                                                bgcolor: 'primary.main'
                                                            }}
                                                        >
                                                            <EditIcon />
                                                        </Avatar>

                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                                Modifica squadra
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {formatDateTime(activity.createdAt)}
                                                            </Typography>
                                                        </Box>

                                                        <Chip
                                                            icon={status.icon}
                                                            label={status.label}
                                                            color={status.color}
                                                            size="small"
                                                        />
                                                    </Box>

                                                    {/* Informazioni utente e sistema */}
                                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                                        <Grid item xs={12} md={6}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                                <Typography variant="body2">
                                                                    <strong>Utente:</strong> {activity.user_email || 'N/A'}
                                                                </Typography>
                                                            </Box>
                                                            {activity.user_nome && (
                                                                <Typography variant="body2" color="text.secondary" sx={{ ml: 3 }}>
                                                                    {activity.user_nome} {activity.user_cognome}
                                                                </Typography>
                                                            )}
                                                        </Grid>

                                                        <Grid item xs={12} md={6}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <ComputerIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                                <Typography variant="body2">
                                                                    <strong>IP:</strong> {activity.ip_address || 'N/A'}
                                                                </Typography>
                                                            </Box>
                                                        </Grid>
                                                    </Grid>

                                                    <Divider sx={{ my: 2 }} />

                                                    {/* Dettagli dei cambiamenti */}
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                        <CompareArrowsIcon sx={{ color: 'primary.main' }} />
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                            Dettagli modifiche
                                                        </Typography>
                                                    </Box>

                                                    <ChangeComparison
                                                        previousData={details.previousData}
                                                        updatedData={details.updatedData}
                                                        patchData={details.patchData}
                                                    />

                                                    {/* Informazioni tecniche */}
                                                    <Box sx={{ mt: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            <strong>ID Log:</strong> {activity.id} |
                                                            <strong> Action:</strong> {activity.action} |
                                                            <strong> Method:</strong> {activity.method?.toUpperCase()}
                                                        </Typography>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </motion.div>
                                    );
                                })}
                            </AccordionDetails>
                        </Accordion>
                    </motion.div>
                ))}
            </Box>

            {/* Footer */}
            <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    Ultimo aggiornamento: {new Date().toLocaleString('it-IT')}
                </Typography>
            </Box>
        </Box>
    );
};